import type { GameOverMenuConfig, GameOverRubberConfig } from "./GameOverMenu";
import { ContractBid } from "../bidding-menu/ContractBid";
import { GameType } from "../../../Types";
import { GameVars } from "../../../GameVars";

export class GameOverSummary extends Phaser.GameObjects.Container {

    private static readonly WIDTH = 860;
    private static readonly HEADER_HEIGHT = 40;
    private static readonly ROW_HEIGHT = 46;
    private static readonly RUBBER_COLUMNS = [
        { title: "GAME", x: -380, width: 70 },
        { title: "HANDS", x: -275, width: 80 },
        { title: "WINNER", x: -135, width: 120 },
        { title: "GAME SCORE", x: 30, width: 150 },
        { title: "RUBBER SCORE", x: 205, width: 160 },
        { title: "TOTAL", x: 355, width: 110 }
    ];
    private static readonly CHICAGO_COLUMNS = [
        { title: "HAND", x: -370 },
        { title: "CONTRACT", x: -245 },
        { title: "DECLARER", x: -95 },
        { title: "RESULT", x: 75 },
        { title: "N - S", x: 230 },
        { title: "E - W", x: 365 }
    ];

    constructor(scene: Phaser.Scene, public readonly config: GameOverMenuConfig) {

        super(scene);

        if (config.gameType === GameType.RUBBER) {
            this.renderRubber(config);
            return;
        }

        const headerHeight = GameOverSummary.HEADER_HEIGHT;
        const rowHeight = GameOverSummary.ROW_HEIGHT;
        const columns = GameOverSummary.CHICAGO_COLUMNS;
        for (const hand of config.hands) {
            if (!hand.points || !Number.isFinite(hand.points.northSouth) || !Number.isFinite(hand.points.eastWest)) {
                throw new Error(`Missing or invalid Chicago points for hand ${hand.handNumber}`);
            }
        }
        this.addTableBackground(config.hands.length, rowHeight);

        columns.forEach(column => {
            this.add(this.createLabel(column.x, headerHeight / 2, column.title, 22, "#69AFD6"));
        });

        config.hands.forEach((hand, index) => {
            const row = new Phaser.GameObjects.Container(
                scene, 0, headerHeight + (index + .5) * rowHeight
            );
            const scoreBackground = new Phaser.GameObjects.Graphics(scene);
            row.add(scoreBackground);

            const difference = hand.contract ? hand.declarerTricks - (hand.contract.level + 6) : 0;
            const result = !hand.contract ? "Passed out" : difference < 0 ? `Down (${difference})`
                : difference > 0 ? `Made (+${difference})` : "Made";
            const seats = ["N", "E", "S", "W"];
            const values = [
                `${hand.handNumber}`, hand.contract ? "" : "--",
                hand.contract ? seats[hand.contract.declarer] : "--", result,
                this.formatPoints(config.hands[index].points.northSouth),
                this.formatPoints(config.hands[index].points.eastWest)
            ];
            values.forEach((value, column) => {
                const x = columns[column].x;
                if (column === 1 && hand.contract) {
                    const contract = new ContractBid(scene, hand.contract, { width: 120, height: 48 });
                    contract.setPosition(x, 0).setScale(.75);
                    row.add(contract);
                } else {
                    const pointCell = column >= 4;
                    if (pointCell) {
                        this.drawPill(scoreBackground, x, 120, column === 4 && value.startsWith("+"));
                    }
                    const label = this.createLabel(x, 0, value, 22,
                        column === 0 || (pointCell && value !== "0") ? "#FFFFFF" : "#B8DEF4");
                    if (pointCell) {
                        label.setScale(Math.min(1, 104 / label.width),
                            Math.min(1, 30 / label.height));
                    }
                    row.add(label);
                }
            });
            this.add(row);
        });
    }

    private renderRubber(config: GameOverRubberConfig): void {

        const rows = this.getRubberRows(config);
        const headerHeight = GameOverSummary.HEADER_HEIGHT;
        const rowHeight = 60;
        this.addTableBackground(rows.length, rowHeight);
        GameOverSummary.RUBBER_COLUMNS.forEach(column => {
            const label = this.createLabel(column.x, headerHeight / 2, column.title, 20, "#69AFD6");
            label.setScale(Math.min(1, column.width / label.width), 1);
            this.add(label);
        });
        rows.forEach(({ game, bonus, total }, index) => {
            const row = new Phaser.GameObjects.Container(this.scene, 0, headerHeight + (index + .5) * rowHeight);
            const northSouth = game.winningPartnership === "northSouth";
            const graphics = new Phaser.GameObjects.Graphics(this.scene);
            this.drawPill(graphics, -135, 120, northSouth, 40);
            this.drawPill(graphics, 355, 110, northSouth, 40, true);
            row.add(graphics);
            const values = [
                `${index + 1}`, `${game.handCount}`, northSouth ? "N - S" : "E - W",
                this.formatPoints(game.points[game.winningPartnership]),
                `+${GameVars.formatNumber(bonus)}`, GameVars.formatNumber(total)
            ];
            values.forEach((value, column) => {
                const { x, width: cellWidth } = GameOverSummary.RUBBER_COLUMNS[column];
                const color = column === 5 ? (northSouth ? "#FFDA72" : "#FFFFFF")
                    : column === 3 ? "#7CFF96"
                    : column === 4 ? (bonus && northSouth ? "#7CFF96" : "#B8DEF4") : "#FFFFFF";
                const label = this.createLabel(x, column === 4 && bonus ? -8 : 0, value,
                    column === 5 ? 28 : 24, color);
                label.setScale(Math.min(1, (cellWidth - 16) / label.width), 1);
                row.add(label);
            });
            if (bonus) {
                row.add(this.createLabel(205, 15, "(RUBBER BONUS)", 12, "#B8DEF4"));
            }
            this.add(row);
        });
    }

    private getRubberRows(config: GameOverRubberConfig) {

        if (!Array.isArray(config.games) || config.games.length < 2 || config.games.length > 3) {
            throw new Error("Rubber requires two or three completed games");
        }
        const gamesWon = { northSouth: 0, eastWest: 0 };
        const totals = { northSouth: 0, eastWest: 0 };
        const rows = config.games.map((game, index) => {
            if (gamesWon.northSouth === 2 || gamesWon.eastWest === 2) {
                throw new Error("Rubber cannot continue after a partnership wins two games");
            }
            const winner = game.winningPartnership;
            if ((winner !== "northSouth" && winner !== "eastWest")
                || !Number.isSafeInteger(game.handCount) || game.handCount < 1) {
                throw new Error(`Invalid Rubber game ${index + 1}`);
            }
            for (const partnership of ["northSouth", "eastWest"] as const) {
                const contract = game.contractPoints?.[partnership];
                const points = game.points?.[partnership];
                if (!Number.isSafeInteger(contract) || contract < 0
                    || !Number.isSafeInteger(points) || points < contract
                    || (partnership === winner ? contract < 100 : contract >= 100)) {
                    throw new Error(`Invalid Rubber points for game ${index + 1}`);
                }
                totals[partnership] += points;
            }
            gamesWon[winner]++;
            const loser = winner === "northSouth" ? "eastWest" : "northSouth";
            const bonus = gamesWon[winner] === 2 ? (gamesWon[loser] === 0 ? 700 : 500) : 0;
            totals[winner] += bonus;
            return { game, bonus, total: totals[winner] };
        });
        if (gamesWon[config.winningPartnership] !== 2) {
            throw new Error("Rubber winner must have won two games");
        }
        for (const partnership of ["northSouth", "eastWest"] as const) {
            if (config.scores[partnership].gamesWon !== gamesWon[partnership]
                || config.scores[partnership].totalPoints !== totals[partnership]) {
                throw new Error(`Rubber scores do not match game history for ${partnership}`);
            }
        }
        return rows;
    }

    private addTableBackground(rowCount: number, rowHeight: number): void {

        const width = GameOverSummary.WIDTH;
        const headerHeight = GameOverSummary.HEADER_HEIGHT;
        this.setSize(width, headerHeight + rowCount * rowHeight);
        const background = new Phaser.GameObjects.Graphics(this.scene);
        background.fillStyle(0x031a36, 1).fillRoundedRect(-width / 2, 0, width, this.height, 18);
        for (let index = 0; index < rowCount; index++) {
            const last = index === rowCount - 1;
            background.fillStyle(index % 2 === 0 ? 0x0b3c68 : 0x052747, 1);
            background.fillRoundedRect(-width / 2 + 2, headerHeight + index * rowHeight, width - 4,
                rowHeight - (last ? 2 : 0), last ? { tl: 0, tr: 0, bl: 16, br: 16 } : 0);
        }
        background.lineStyle(2, 0x008fce, 1).strokeRoundedRect(-width / 2, 0, width, this.height, 18);
        this.add(background);
    }

    private drawPill(
        graphics: Phaser.GameObjects.Graphics, x: number, width: number, green: boolean,
        height = 32, total = false
    ): void {

        graphics.fillStyle(green ? (total ? 0x003b2d : 0x006333) : 0x06335b, 1);
        graphics.fillRoundedRect(x - width / 2, -height / 2, width, height, 16);
        graphics.lineStyle(2, green ? 0x0be266 : 0x009fff, 1);
        graphics.strokeRoundedRect(x - width / 2, -height / 2, width, height, 16);
    }

    private formatPoints(points: number): string {

        return `${points > 0 ? "+" : ""}${GameVars.formatNumber(points)}`;
    }

    private createLabel(
        x: number, y: number, text: string, fontSize: number, color: string
    ): Phaser.GameObjects.Text {

        return new Phaser.GameObjects.Text(this.scene, x, y, text, {
            fontFamily: "Baloo2-Bold", fontSize: `${fontSize}px`, color, align: "center"
        }).setOrigin(.5);
    }
}
